import Button from "@/common/components/Button";
import MaskedTextField from "@/common/components/MaskedTextField";
import Modal from "@/common/components/Modal";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import SelectController from "@/common/components/SelectController";
import useDebounce from "@/common/hooks/useDebounce";
import { Mapper } from "@/common/services/mapper";
import { FormTransformers } from "@/common/utils/formTransformers";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useCreateTefProductCode } from "../../hooks/useCreateTefProductCode";
import { tefProductCodeCreateSchema, TefProductCodeCreateValidationSchema } from "../../schemas/tefProductCode.schema";
import { tefProviders } from "../../constants/tefModels.constants";
import { numericRegexMask } from "@/common/constants/masks.constant";
import { useProducts } from "@/common/hooks/queries/useProducts";
import { ProductData } from "@/common/types/product.type";

interface TefConfigurationProductCreateModalProps {
  provider?: string;
}

const TefConfigurationProductCreateModal = ({ provider }: TefConfigurationProductCreateModalProps) => {
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const [searchProducts, setSearchProducts] = useDebounce('');

  const {
    mutate: createTefProductCode,
    isPending: createTefProductCodeIsPending,
    isError: createTefProductCodeIsError,
  } = useCreateTefProductCode({
    provider,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tefProductCodes'] });
      setMessages(['Produto adicionado com sucesso!']);
      setIsOpen(false);
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError, setMessages })(error);
    },
  })

  const {
    control,
    handleSubmit,
    setError,
    reset,
    formState: { errors }
  } = useForm<TefProductCodeCreateValidationSchema>({
    resolver: zodResolver(tefProductCodeCreateSchema),
  });

  const onSubmit: SubmitHandler<TefProductCodeCreateValidationSchema> = (data) => createTefProductCode(data);

  const { data: productsPrice } = useProducts({
    filters: {
      search: searchProducts,
      page: 1,
    },
    mapper: Mapper.mapToOptions<ProductData>({
      labelFieldName: "display_name",
      valueFieldName: "id",
    }),
  });

  useEffect(reset, [isOpen]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        Trigger={(
          <Button
            name="create_tef_product_code"
            operationKlassToCheckPermission="Establishment::TefProductCodes::Create"
          >
            Adicionar novo produto
          </Button>
        )}
        dataTestid="submit_create_tef_product_code"
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonIsLoading={createTefProductCodeIsPending}
        disabled={createTefProductCodeIsPending}
        footer="Confirmar adição e salvar alteração"
        title="Adicionar novo produto"
        body={(
          <div className="flex flex-col gap-y-4 text-white-800">
            <SelectController
              control={control}
              name="product_id"
              label="Produto"
              options={productsPrice}
              onInputChange={setSearchProducts}
              markAsRequired
              errorMessage={errors.product_id?.message || undefined}
            />

            <Controller
              control={control}
              name="code"
              render={({ field }) => (
                <MaskedTextField
                  {...field}
                  label={`Código ${provider ? tefProviders[provider]?.label : ''}`}
                  mask={provider ? tefProviders[provider].regexMaskFn(tefProviders[provider].codeLength) : numericRegexMask(6)}
                  prepareChar={(char) => char.toUpperCase()}
                  markAsRequired
                  errorMessage={errors.code?.message || undefined}
                />
              )}
            />
          </div>
        )}
      />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={createTefProductCodeIsError}
        />
      )}
    </>
  )
}

export default TefConfigurationProductCreateModal;
